import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import _, { set } from "lodash";
import {
  isCollectionNullOrEmpty,
  getErrorMessage,
} from "services/UtilityService";
import { sendAuthenticatedAsyncRequest, sendAuthenticatedAsyncRequestWithFinallyCallback } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { formatDateWithName } from "services/MomentUtils";
import "../../../assets/css/switch.css";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import {
  UNPROCESSED_FAX_LIST_COLUMNS,
  UNPROCESSED_FAX_LIST_HIDDEN_COLUMNS,
  UNKNOWN,
  DEFAULT_UNPROCESSED_FAX_LIST_SORTING,
  DEFAULT_UNPROCESSED_FAX_LIST_FILTER,
  UNPROCESSED_FAX_PRIORITY,
} from "lookup/UnprocessedFaxListConstantsV2";
import { UNPROCESSED_FAX_PROGRAM_TYPE } from "lookup/UnprocessedFaxListConstantsV2";
import { Tip, renderDateCell } from "commons/utils";

const DEFAULT_PAGE_SIZE = 15;
const SESSION_KEY = "unprocessedFaxPaginationState";
const POLL_TIMEOUT_SECONDS = 45;

const renderLastModifiedDateCol = (date, name) => date ? renderDateCell(formatDateWithName(date, name)) : <span></span>;

const getFaxProgramType = (isSwitchProgram) => {
  if (isSwitchProgram) {
    return UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
  } else {
    return UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH;
  }
}

const getFaxTypeForRoute = (programType) => {
  switch (programType) {
    case UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH:
      return "Switch";
    case UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH:
      return "NonSwitch";
    case UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN:
    default:
      return "Unknown";
  }
}

const buildPrimaryFilters = (currentFilters, urlParams, faxProgramType) => {
  let newFilters = currentFilters.filter(
    (f) => !["tags", "faxPriority"].includes(f.id)
  );

  // Check if associatedSwitchId is true for Switch faxes
  const associatedSwitchFilter = currentFilters.find(
    (f) => f.id === "associatedSwitchId"
  );
  const isAssociatedSwitchTrue = 
    associatedSwitchFilter && 
    (associatedSwitchFilter.value === true || associatedSwitchFilter.value === "true");
  const isSwitchFax = faxProgramType === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
  
  // Only add tags and priority filters if not a Switch fax with associatedSwitchId = true
  const shouldSkipTagsAndPriority = isSwitchFax && isAssociatedSwitchTrue;

  const faxProgramCode = urlParams.faxProgramCode;
  const faxPriorityValue =
    UNPROCESSED_FAX_PRIORITY[urlParams.faxPriority?.toUpperCase()];

  if (faxProgramCode && !shouldSkipTagsAndPriority)
    newFilters.push({
      id: "tags",
      value: [faxProgramCode],
      isdefault: true,
    });

  if (faxPriorityValue && !shouldSkipTagsAndPriority)
    newFilters.push({
      id: "faxPriority",
      value: faxPriorityValue,
      isdefault: true,
    });
  
  if (faxProgramType)
    newFilters.push({
      id: "faxProgramType",
      value: faxProgramType,
      isdefault: true,
    });
  

  return newFilters;
};

const filterModification = (filtered) => {
  let filteredDeepCopy = _.cloneDeep(filtered);

  if (filteredDeepCopy && filteredDeepCopy.length > 0) {
    const showMyRecentsFilter = filteredDeepCopy.find(
      (f) => f.id === "showMyRecentsOnly"
    );

    if (showMyRecentsFilter && showMyRecentsFilter.value === true) {
      const idx = filteredDeepCopy.findIndex((f) => f.id === "recentlyViewedUsers");
      if (idx > -1) filteredDeepCopy.splice(idx, 1);
    } else {
      const idx = filteredDeepCopy.findIndex((f) => f.id === "recentlyViewedUsers");
      if (idx > -1) {
        const filter = filteredDeepCopy[idx];
        if (filter.value.length > 0) {
          filteredDeepCopy[idx].value = filter.value.map((u) => u.id);
        } else filteredDeepCopy.splice(idx, 1);
      }
    }

    const tagsIndex = filteredDeepCopy.findIndex((f) => f.id === "tags");
    if (tagsIndex > -1) {
      const filter = filteredDeepCopy[tagsIndex];
      if (
        filter.value.length > 0 &&
        typeof filter.value[0] === "object" &&
        filter.value[0] !== null
      ) {
        filteredDeepCopy[tagsIndex].value = filter.value.map((t) => t.id);
      }
    }

    filteredDeepCopy = filteredDeepCopy.filter(
      (f) =>
        !(f.value instanceof Array) ||
        (f.value instanceof Array && !isCollectionNullOrEmpty(f.value))
    );
  }

  return filteredDeepCopy;
};

const sortedModification = (sorted) => {
  const sortedCopy = _.cloneDeep(sorted);
  if (sortedCopy && sortedCopy.length > 0) {
    const idx = sortedCopy.findIndex((f) => f.id === "recentlyViewedUsers");
    if (idx > -1) sortedCopy[idx].id = "lastViewedBy";
  }
  return sortedCopy;
};

const UnprocessedFaxList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pagination, setPagination] = useState(() => {
    const shouldReset = location.state?.reset;
    if (shouldReset) {
      sessionStorage.removeItem(SESSION_KEY);
    }

    const saved = sessionStorage.getItem(SESSION_KEY);
    if (!shouldReset && saved) return JSON.parse(saved);

    return {
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      sorted: DEFAULT_UNPROCESSED_FAX_LIST_SORTING,
      filtered: [],
      resized: [],
      expanded: {},
    };
  });

  const [faxList, setFaxList] = useState([]);
  const [totalFaxes, setTotalFaxes] = useState("Loading ...");
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [switchAdminList, setSwitchAdminList] = useState([]);
  const [faxTagList, setFaxTagList] = useState([]);
  const [programName, setProgramName] = useState("");
  const [programType, setProgramType] = useState("");
  const [listColumns, setListColumns] = useState([]);
  const [lastModifiedDate, setLastModifiedDate] = useState(null);
  
  // Refs to manage polling lifecycle
  const pollingActiveRef = useRef(false);
  const pollingTimeoutRef = useRef(null);
  // Ref to always have the latest lastModifiedDate value (avoids closure issues)
  const lastModifiedDateRef = useRef(null);
  // Abort in-flight /ufl/list when filters/pagination change or a newer list request starts
  const listFetchAbortControllerRef = useRef(null);

  const urlParamsObject = new URLSearchParams(location.search);
  const urlParams = {
    faxProgramCode: urlParamsObject.get("faxProgramCode"),
    faxPriority: urlParamsObject.get("faxPriority"),
  };

  // --- persist pagination in sessionStorage ---
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(pagination));
  }, [pagination]);

  // --- sync ref with state to ensure they stay in sync ---
  useEffect(() => {
    lastModifiedDateRef.current = lastModifiedDate;
  }, [lastModifiedDate]);

  // Helper function to get the maximum lastModifiedDate from records
  const getMaxLastModifiedDate = (records) => {
    if (!records || records.length === 0) return null;
    
    const dates = records
      .map(record => record.lastModifiedDate)
      .filter(date => date != null)
      .map(date => new Date(date));
    
    if (dates.length === 0) return null;
    
    const maxDate = new Date(Math.max(...dates));
    return maxDate.toISOString();
  };

  // --- API call replacement for updateUnprocessedFaxList ---
  const fetchUnprocessedFaxList = useCallback(async (state, isPolling = false) => {
    listFetchAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    listFetchAbortControllerRef.current = abortController;

    // Don't show loading spinner for polling requests to avoid UI flicker
    if (!isPolling) {
      setLoading(true);
    }
    setIsError(false);
    const filteredDeepCopy = filterModification(state.filtered);
    const sortedDeepCopy = sortedModification(state.sorted);

    // Build request payload
    const requestPayload = {
      pageSize: state.pageSize,
      page: state.page,
      sorted: sortedDeepCopy,
      filtered: filteredDeepCopy,
    };

    // Add long polling parameters if this is a polling request
    // Use ref to get the latest value (avoids closure issues)
    const currentLastModifiedDate = lastModifiedDateRef.current;
    if (isPolling && currentLastModifiedDate) {
      requestPayload.lastModifiedDate = currentLastModifiedDate;
      requestPayload.pollTimeoutSeconds = POLL_TIMEOUT_SECONDS;
    }

    sendAuthenticatedAsyncRequestWithFinallyCallback(
      "/ufl/list",
      "POST",
      requestPayload,
      ({data}) => {
        const records = data.records || [];
        const lastModifiedDateForNextPoll = data.lastModifiedDateForNextPoll;
        
        // Update lastModifiedDate based on backend response.
        // On poll: lastModifiedDateForNextPoll === null means long-poll timed out with no change
        // to records (watermark unchanged). Non-null means the server advanced the cursor.
        if (lastModifiedDateForNextPoll != null) {
          setLastModifiedDate(lastModifiedDateForNextPoll);
          lastModifiedDateRef.current = lastModifiedDateForNextPoll;
        }

        // Poll with null lastModifiedDateForNextPoll = no change (e.g. long-poll timeout). Do not
        // apply records/totals — empty payload must not clear the grid.
        if (isPolling && lastModifiedDateForNextPoll == null) {
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }
          pollingTimeoutRef.current = setTimeout(() => {
            if (pollingActiveRef.current && lastModifiedDateRef.current) {
              fetchUnprocessedFaxList(pagination, true);
            }
          }, 100);
          return;
        }

        // Initial load, or poll where the server advanced the watermark — apply list payload
        setFaxList(records);
        setTotalPages(data.totalPages || 0);
        setTotalFaxes(data.totalElements || 0);
        
        // For initial load (non-polling), if backend didn't provide lastModifiedDateForNextPoll,
        // fall back to calculating from records as a backup
        if (!isPolling && !lastModifiedDateForNextPoll) {
          const maxDateFromNewRecords = getMaxLastModifiedDate(records);
          if (maxDateFromNewRecords) {
            // Use the max date from records as fallback for initial load
            setLastModifiedDate(maxDateFromNewRecords);
            lastModifiedDateRef.current = maxDateFromNewRecords;
          } else {
            // For initial load, if no records have lastModifiedDate, set to null
            // This prevents polling from starting if we can't track modifications
            setLastModifiedDate(null);
            lastModifiedDateRef.current = null;
          }
        }

        // If this was a polling request and we got updated data, immediately poll again.
        // The poll-timeout case (lastModifiedDateForNextPoll null) already scheduled the next poll above.
        if (isPolling) {
          // Clear any existing timeout
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }
          
          // Got updated data - immediately poll again to check for more updates
          // Use a small delay to avoid rapid-fire requests, but much faster than timeout
          pollingTimeoutRef.current = setTimeout(() => {
            if (pollingActiveRef.current && lastModifiedDateRef.current) {
              fetchUnprocessedFaxList(pagination, true);
            }
          }, 100);
        }
      },
      (err) => {
        // On error during polling, stop polling but don't show error notification
        // to avoid spamming the user
        if (!isPolling) {
          setFaxList([]);
          setTotalFaxes(0);
          setTotalPages(0);
          setIsError(true);
          showNotification({
            title: `Error fetching fax list`,
            message:
              getErrorMessage(err.data) ||
              getErrorMessage("SOMETHING_WENT_WRONG"),
            position: "tr",
            type: "error",
          });
        } else {
          // Stop polling on error
          pollingActiveRef.current = false;
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
        }
      },
      () => {
        if (!isPolling) {
          setLoading(false);
        }
      },
      abortController.signal
    );
  }, [lastModifiedDate, pagination]);

  // --- setup initial filters + program details ---
  const setPrimaryDetails = useCallback(() => {
    const faxProgramCode = urlParams.faxProgramCode;
    if (faxProgramCode && faxProgramCode !== UNKNOWN) {
      setLoading(true);
      sendAuthenticatedAsyncRequestWithFinallyCallback(
        `/fax-program/${faxProgramCode}`,
        "GET",
        null,
        (res) => {
          const { programName, isSwitchProgram = false } = res.data;
          const defaultFilters = buildPrimaryFilters(
            DEFAULT_UNPROCESSED_FAX_LIST_FILTER.filter(filter => !filter.faxProgramType || (filter.faxProgramType && filter.faxProgramType.includes(
                getFaxProgramType(isSwitchProgram)))),
            urlParams,
            getFaxProgramType(isSwitchProgram)
          );
          setProgramName(`${programName} Fax`);
          setProgramType(getFaxProgramType(isSwitchProgram));
          setPagination((prev) => ({ ...prev, filtered: defaultFilters }));
          updateData({ ...pagination, filtered: defaultFilters });
        },
        (err) => {
          showNotification({
            title: `Unable to fetch fax program (${faxProgramCode}) details`,
            message:
              getErrorMessage(err.data) ||
              getErrorMessage("SOMETHING_WENT_WRONG"),
            position: "tr",
            type: "error",
          });
        },
        () => {
          setLoading(false);
        }
      );
    } else {
      setProgramName("Unknown Fax");
      setProgramType(UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN);
      const defaultFilters = buildPrimaryFilters(
            DEFAULT_UNPROCESSED_FAX_LIST_FILTER.filter(filter => !filter.faxProgramType || (filter.faxProgramType && filter.faxProgramType.includes(
                UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN))),
            urlParams,
            UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN
          );
      setPagination((prev) => ({ ...prev, filtered: defaultFilters }));
      updateData({ ...pagination, filtered: defaultFilters});
    }
  }, [location.search, pagination.filtered]);

  const getLockColumnSelection = () => {
    const lockedColumns = [];
    const associatedSwitchFilter = pagination.filtered.find(
      (f) => f.id === "associatedSwitchId"
    );
    const isAssociatedSwitchTrue = 
      associatedSwitchFilter && 
      (associatedSwitchFilter.value === true || associatedSwitchFilter.value === "true");
    const isSwitchFax = programType === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
    
    // Lock tags if from URL params
    if (urlParams.faxProgramCode) {
      lockedColumns.push({ id: "tags", value: [urlParams.faxProgramCode].filter(Boolean) });
    }
    
    // Only lock priority if from URL params AND not a Switch fax with associatedSwitchId = true
    if (urlParams.faxPriority && !(isSwitchFax && isAssociatedSwitchTrue)) {
      lockedColumns.push({ id: "faxPriority" });
    }
    
    return lockedColumns;
  }

  const updateData = useCallback(
    (state, preventUpdate = false, isPolling = false) => {
      if (!preventUpdate) {
        // Stop polling when filters/pagination change
        if (!isPolling) {
          pollingActiveRef.current = false;
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          // Reset lastModifiedDate when filters/pagination change
          setLastModifiedDate(null);
          lastModifiedDateRef.current = null;
        }
        fetchUnprocessedFaxList(state, isPolling);
      }
    },
    [fetchUnprocessedFaxList]
  );

  // Start polling after initial data load
  const startPolling = useCallback(() => {
    // Only start polling if we have a lastModifiedDate and polling is not already active
    if (!lastModifiedDate || pollingActiveRef.current) {
      return;
    }

    pollingActiveRef.current = true;

    // Start first poll
    fetchUnprocessedFaxList(pagination, true);
  }, [lastModifiedDate, pagination, fetchUnprocessedFaxList]);

  // Stop polling when component unmounts or filters change
  useEffect(() => {
    return () => {
      pollingActiveRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      listFetchAbortControllerRef.current?.abort();
      listFetchAbortControllerRef.current = null;
    };
  }, []);

  // Start polling when we have a modification waterline (including empty list — new faxes can arrive)
  useEffect(() => {
    if (lastModifiedDate && !loading && !pollingActiveRef.current) {
      startPolling();
    }
  }, [lastModifiedDate, loading, startPolling]);

  const onUpdateTable = (filterValues, sortBy, pageSize, pageNo) => {
    const associatedSwitchFilter = filterValues.find(
      (f) => f.id === "associatedSwitchId"
    );
    const previousAssociatedSwitchFilter = pagination.filtered.find(
      (f) => f.id === "associatedSwitchId"
    );
    const isAssociatedSwitchTrue = 
      associatedSwitchFilter && 
      (associatedSwitchFilter.value === true || associatedSwitchFilter.value === "true");
    const wasAssociatedSwitchTrue = 
      previousAssociatedSwitchFilter && 
      (previousAssociatedSwitchFilter.value === true || previousAssociatedSwitchFilter.value === "true");
    const isSwitchFax = programType === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
    
    let processedFilterValues = [...filterValues];
    
    // If switching from false to true, remove only default/pre-applied tags and priority filters
    if (isSwitchFax && isAssociatedSwitchTrue && !wasAssociatedSwitchTrue) {
      // Remove only filters that are marked as default (pre-applied from URL)
      processedFilterValues = filterValues.filter(
        (f) => !(["tags", "faxPriority"].includes(f.id) && f.isdefault === true)
      );
    }
    // If switching from true to false, restore URL-based filters
    else if (isSwitchFax && !isAssociatedSwitchTrue && wasAssociatedSwitchTrue) {
      const faxProgramCode = urlParams.faxProgramCode;
      const faxPriorityValue =
        UNPROCESSED_FAX_PRIORITY[urlParams.faxPriority?.toUpperCase()];
      
      // Remove existing tags and priority filters first
      processedFilterValues = processedFilterValues.filter(
        (f) => !["tags", "faxPriority"].includes(f.id)
      );
      
      // Restore URL-based filters if they exist
      if (faxProgramCode) {
        processedFilterValues.push({
          id: "tags",
          value: [faxProgramCode],
          isdefault: true,
        });
      }
      
      if (faxPriorityValue) {
        processedFilterValues.push({
          id: "faxPriority",
          value: faxPriorityValue,
          isdefault: true,
        });
      }
    }

    const newState = {
      ...pagination,
      pageSize,
      page: pageNo,
      sorted: sortBy || [],
      filtered: processedFilterValues,
    };
    setPagination(newState);
    updateData(newState);
  };

  const handleToggle = useCallback(() => {
    setPagination((prevPagination) => {
      const hasShowRecentsFilter = prevPagination.filtered.some(
        (filter) => filter.id === "showMyRecentsOnly"
      );

      const updatedFiltered = hasShowRecentsFilter
        ? prevPagination.filtered.map((filter) =>
            filter.id === "showMyRecentsOnly"
              ? { ...filter, value: !filter.value }
              : filter
          )
        : [
            ...prevPagination.filtered,
            { id: "showMyRecentsOnly", value: true },
          ];

      const updatedState = { ...prevPagination, filtered: updatedFiltered };
      updateData(updatedState);
      return updatedState;
    });
  }, [updateData]);

  const isShowMyRecentsDisabled = useCallback(() => {
    const filter = pagination.filtered.find((f) => f.id === "showMyRecentsOnly");
    return filter ? filter.value : false;
  }, [pagination.filtered]);

  useEffect(() => {
    setPrimaryDetails();
    sendAuthenticatedAsyncRequest(
      "/fax/unprocessed-fax-list-filters",
      "GET",
      null,
      (res) => {
        if (res.status === 200) {
          const { recentlyViewedUsers, enabledFaxTags } = res.data.filters;
          const switchAdmins = recentlyViewedUsers.map((e) => ({
            id: e.value,
            name: e.id,
          }));
          const faxTags = enabledFaxTags.map((e) => ({
            id: e.value,
            name: e.id,
          }));

          switchAdmins.sort((a, b) => a.name.localeCompare(b.name));
          setSwitchAdminList(switchAdmins);
          setFaxTagList(faxTags);
        }
      },
      (res) => {
        showNotification({
          title: "Unable to fetch filters!",
          message:
            getErrorMessage(res.data) ||
            getErrorMessage("SOMETHING_WENT_WRONG"),
          position: "tr",
          type: "error",
        });
      }
    );
  }, []);

  useEffect(() => {
    if (isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      setIsError(false);
    }
  }, [isError]);

  useEffect(() => {
    setListColumns(
      UNPROCESSED_FAX_LIST_COLUMNS(
        faxTagList,
        switchAdminList,
        handleToggle,
        isShowMyRecentsDisabled,
        renderLastModifiedDateCol,
        programName,
        UNPROCESSED_FAX_LIST_HIDDEN_COLUMNS[programType],
        getLockColumnSelection(),
      )
    );
  }, [programName, faxTagList, switchAdminList, programType, handleToggle, isShowMyRecentsDisabled]);

  return (
    <Container className="list-container" fluid>
      <Row>
        <Col md={12}>
          <div className="codex-version-display-bar">
            <span style={{ float: "left" }}>
              Total Unprocessed Faxes: <strong>{totalFaxes}</strong>
            </span>
            <strong style={{ float: "center" }}>Unprocessed Fax List</strong>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ServerSideReactTable
            columnDefination={listColumns}
            data={faxList}
            currentPage={pagination.page}
            sorted={pagination.sorted}
            filters={pagination.filtered}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            totalPages={totalPages}
            totalRecords={totalFaxes === "loading" ? 0 : totalFaxes}
            onUpdateTableData={onUpdateTable}
            loading={loading}
            onRowClick={(rowInfo) => {
              if (rowInfo) {
                const faxType = getFaxTypeForRoute(programType);
                navigate(
                  `/unprocessed-fax/fax-details-v2/${faxType}/${rowInfo.original.faxInfoIdentifier}`
                );
              }
            }}
          />
          <Tip>Tip: Hold shift when sorting to multi-sort and disable sort!</Tip>
        </Col>
      </Row>
    </Container>
  );
};

export default UnprocessedFaxList;
